/**
 * Pricing Page — Subscription Tiers
 *
 * Matches the guardrail.tech/pricing marketing site layout:
 * - Deep navy hero with cyan accent label
 * - Pill-shaped billing toggle (TogglePillGroup from bodykit)
 * - Dark navy cards with cyan accents (Card from bodykit)
 * - White enterprise section
 * - Pill-shaped CTA buttons (Button from bodykit)
 *
 * Data is fetched live from Stripe via /v2/pricing.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { TrafficLightLogo } from '@/components/TrafficLightLogo';
import {
  Check,
  X,
  FlaskConical,
  AlertTriangle,
  CheckSquare,
} from 'lucide-react';
import { AppHeader } from '@/components/AppHeader';
import {
  Badge as BkBadge,
  Button as BkButton,
  Card as BkCard,
  TogglePillGroup,
  LoadingSpinner,
  cn,
} from '@grt/bodykit';
import { apiClient } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useUserInfo } from '@/hooks/useUserInfo';
import { usePromoConfig } from '@/hooks/usePromoConfig';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PricingTier {
  tier: string;
  name: string;
  description?: string;
  monthlyPrice: number;
  yearlyPrice: number;
  yearlyDiscount: number;
  trialDays?: number;
  features: { name: string; included: boolean | string | number }[];
  popular?: boolean;
  ctaText: string;
  ctaUrl: string;
  stripeProductId?: string;
  stripePriceIdMonthly?: string;
  stripePriceIdYearly?: string;
  order?: number;
  active?: boolean;
  missingMetadata?: string[];
  seats?: number | null;
}

interface PricingData {
  tiers: PricingTier[];
  currency: string;
  testMode?: boolean;
  faq: { question: string; answer: string }[];
}

// ---------------------------------------------------------------------------
// Traffic Light Dots (●●●)
// ---------------------------------------------------------------------------

function TrafficDots() {
  return (
    <span className="inline-flex items-center gap-1 mr-2">
      <span className="w-2.5 h-2.5 rounded-full bg-red-500" />
      <span className="w-2.5 h-2.5 rounded-full bg-yellow-500" />
      <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
    </span>
  );
}

// ---------------------------------------------------------------------------
// Feature Row
// ---------------------------------------------------------------------------

function FeatureRow({ feature }: { feature: PricingTier['features'][number] }) {
  const { name, included } = feature;
  const isIncluded = typeof included === 'boolean' ? included : true;

  return (
    <div className="flex items-start gap-2.5 py-0.5">
      {isIncluded ? (
        <Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
      ) : (
        <X className="w-4 h-4 text-muted-foreground flex-shrink-0 mt-0.5" />
      )}
      <span className={cn('text-sm', isIncluded ? 'text-gray-300' : 'text-muted-foreground')}>
        {name}
        {typeof included !== 'boolean' && (
          <>: <span className="font-semibold text-foreground">{included}</span></>
        )}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tier Card — uses bodykit Card, BkButton, BkBadge
// ---------------------------------------------------------------------------

function TierCard({
  tier,
  yearly,
  onSubscribe,
  testMode,
  currentTier,
  isTrial,
}: {
  tier: PricingTier;
  yearly: boolean;
  onSubscribe: (tier: string, interval: 'month' | 'year') => void;
  testMode?: boolean;
  currentTier?: string;
  isTrial?: boolean;
}) {
  const promo = usePromoConfig();
  const interval: 'month' | 'year' = yearly ? 'year' : 'month';
  const isEnterprise = tier.ctaUrl.startsWith('mailto:');
  const isInactive = tier.active === false;
  const isFree = tier.monthlyPrice === 0 && tier.yearlyPrice === 0;
  const isCurrentPlan = currentTier != null && tier.tier === currentTier;
  const isDowngrade = isFree && currentTier != null && currentTier !== 'free';
  const isPro = tier.popular === true;

  // Every paid tier starts with a free trial: show $0* with an asterisk note
  // explaining the price after the trial ends.
  const actualPrice = yearly && tier.yearlyPrice > 0
    ? tier.yearlyPrice / 12
    : tier.monthlyPrice;
  const showPromoPrice = !isFree && !isEnterprise;

  // Asterisk note text
  let asteriskNote = '';
  if (isFree) {
    asteriskNote = `*limited to 10 scans/month after your ${promo.trialLabel} free trial`;
  } else if (showPromoPrice) {
    const priceStr = yearly
      ? `$${(tier.yearlyPrice / 12).toFixed(0)}/month`
      : `$${tier.monthlyPrice.toFixed(0)}/month`;
    asteriskNote = `*${priceStr} after your ${promo.trialLabel} free trial`;
  }

  // Card border: Pro gets primary highlight, others subtle border
  const cardBorder = isPro
    ? 'border-primary shadow-lg shadow-primary/10'
    : '';

  return (
    <BkCard
      className={cn(
        'relative flex flex-col p-6 transition-all duration-200 hover:-translate-y-0.5 backdrop-blur-sm',
        cardBorder,
      )}
    >
      {/* Current Plan badge takes priority over Most Popular */}
      {isCurrentPlan ? (
        <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 z-10">
          <BkBadge className="px-4 py-0.5 text-xs font-semibold uppercase tracking-wide">
            Current Plan{isTrial ? ' (Trial)' : ''}
          </BkBadge>
        </div>
      ) : isPro ? (
        <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 z-10">
          <BkBadge className="px-4 py-0.5 text-xs font-semibold uppercase tracking-wide">
            Most Popular
          </BkBadge>
        </div>
      ) : null}

      {/* Tier name + description */}
      <h3 className="text-lg font-bold text-foreground">{tier.name}</h3>
      {tier.description && (
        <p className="text-muted-foreground text-xs mt-1 leading-snug min-h-[2.5rem]">
          {tier.description}
        </p>
      )}

      {/* Price */}
      <div className="mt-5 mb-1">
        {isEnterprise ? (
          <p className="text-4xl font-bold text-foreground">Custom</p>
        ) : showPromoPrice ? (
          <p className="text-4xl font-bold text-foreground">
            $0<span className="text-base align-top text-primary">*</span>
          </p>
        ) : isFree ? (
          <p className="text-4xl font-bold text-foreground">
            $0<span className="text-base align-top text-primary">*</span>
          </p>
        ) : (
          <div className="flex items-baseline gap-1">
            <span className="text-4xl font-bold text-foreground">
              ${actualPrice.toFixed(actualPrice % 1 === 0 ? 0 : 2)}
            </span>
            <span className="text-muted-foreground text-sm">/mo</span>
            {yearly && tier.yearlyPrice > 0 && (
              <span className="text-muted-foreground text-xs ml-1">
                (${tier.yearlyPrice.toFixed(0)}/yr)
              </span>
            )}
          </div>
        )}
      </div>

      {/* Asterisk note — primary accent */}
      {asteriskNote && (
        <p className="text-xs text-primary mb-4 min-h-[1rem]">
          {asteriskNote}
        </p>
      )}
      {!asteriskNote && <div className="mb-4 min-h-[1rem]" />}

      {/* Features */}
      <div className="flex-1 space-y-1 mb-6">
        {tier.features
          .filter((f) => f.name !== 'Team seats')
          .map((feature, i) => (
            <FeatureRow key={i} feature={feature} />
          ))}
        {tier.seats != null && (tier.seats === -1 || tier.seats > 1) && (
          <div className="flex items-start gap-2.5 py-0.5">
            <Check className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
            <span className="text-sm text-gray-300">
              Team seats: <span className="font-semibold text-foreground">
                {tier.seats === -1 ? 'Unlimited' : tier.seats}
              </span>
            </span>
          </div>
        )}
      </div>

      {/* CTA Buttons — using BkButton */}
      {!isEnterprise && isCurrentPlan ? (
        <BkButton variant="outline" size="lg" className="w-full opacity-80" disabled asChild>
          <a href="/account">
            <TrafficDots />
            Current Plan
          </a>
        </BkButton>
      ) : !isEnterprise && isDowngrade ? (
        <BkButton
          variant="outline"
          size="lg"
          className="w-full opacity-60"
          onClick={() => onSubscribe(tier.tier, interval)}
        >
          <TrafficDots />
          Downgrade
        </BkButton>
      ) : !isEnterprise && isFree ? (
        <BkButton
          variant="outline"
          size="lg"
          className="w-full"
          onClick={() => onSubscribe(tier.tier, interval)}
        >
          <TrafficDots />
          Start Free
        </BkButton>
      ) : !isEnterprise ? (
        <BkButton
          variant="default"
          size="lg"
          className="w-full shadow-lg shadow-primary/20"
          onClick={() => onSubscribe(tier.tier, interval)}
        >
          <TrafficDots />
          Start Free
        </BkButton>
      ) : (
        <BkButton variant="default" size="lg" className="w-full shadow-lg shadow-primary/20" asChild>
          <a href={tier.ctaUrl}>
            <TrafficDots />
            Contact Us
          </a>
        </BkButton>
      )}

      {/* Sandbox warnings */}
      {testMode && isInactive && (
        <div className="mt-3 rounded-lg border border-border bg-card/50 px-3 py-2">
          <div className="flex items-center gap-1.5">
            <AlertTriangle className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
            <span className="text-muted-foreground text-[11px] font-semibold uppercase tracking-wide">
              Hidden in production
            </span>
          </div>
        </div>
      )}
      {tier.missingMetadata && tier.missingMetadata.length > 0 && (
        <div className="mt-3 rounded-lg border border-red-500/30 bg-red-500/5 px-3 py-2">
          <div className="flex items-center gap-1.5 mb-1">
            <AlertTriangle className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />
            <span className="text-red-400 text-[11px] font-semibold uppercase tracking-wide">
              Missing Stripe metadata
            </span>
          </div>
          <ul className="space-y-0.5">
            {tier.missingMetadata.map((field) => (
              <li key={field} className="font-mono text-[10px] text-red-400/80">{field}</li>
            ))}
          </ul>
        </div>
      )}
    </BkCard>
  );
}

// ---------------------------------------------------------------------------
// Enterprise Section — WHITE background
// ---------------------------------------------------------------------------

function EnterpriseSection({ tier }: { tier: PricingTier }) {
  // Grab included features for the 3x2 grid
  const includedFeatures = tier.features.filter(
    (f) => (typeof f.included === 'boolean' ? f.included : true),
  );

  return (
    <section className="bg-white py-20 mt-20">
      <div className="container mx-auto px-4 max-w-5xl text-center">
        <h2 className="text-4xl font-bold text-[#1a1a2e] mb-3">Enterprise</h2>
        <p className="text-gray-500 text-lg mb-10">
          {tier.description || 'For businesses with advanced security requirements'}
        </p>

        {/* Feature grid — 3 columns */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-10 max-w-3xl mx-auto">
          {includedFeatures.map((feature, i) => (
            <BkCard
              key={i}
              className="flex flex-col items-center justify-center bg-white p-5 gap-2"
            >
              <CheckSquare className="w-5 h-5 text-primary" />
              <span className="text-sm font-medium text-[#1a1a2e]">
                {feature.name}
                {typeof feature.included !== 'boolean' && (
                  <span className="font-semibold ml-1">{feature.included}</span>
                )}
              </span>
            </BkCard>
          ))}
        </div>

        <BkButton variant="default" size="lg" className="shadow-lg shadow-primary/20" asChild>
          <a href={tier.ctaUrl}>
            <TrafficDots />
            Connect with Us
          </a>
        </BkButton>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function PricingPage() {
  useDocumentTitle('Pricing');
  const [yearly, setYearly] = useState(false);
  const { authenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Fetch user's current subscription tier (only when authenticated)
  const { data: userInfo } = useUserInfo();
  const currentTier = authenticated ? userInfo?.subscription?.tier : undefined;
  const isTrial = authenticated && userInfo?.subscription?.trialEndsAt
    ? new Date(userInfo.subscription.trialEndsAt) > new Date()
    : false;

  const { data: pricingData, isLoading } = useQuery<PricingData>({
    queryKey: ['pricing'],
    queryFn: () => apiClient.getPricing(),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const rawTiers = pricingData?.tiers ?? [];
  const faq = pricingData?.faq ?? [];
  const isTestMode = pricingData?.testMode ?? false;

  // Every account starts with a free trial, so ALL visitors see $0* pricing and
  // the "Start Free" CTA — the trial offer is permanent, not a time-boxed promo.
  const promo = usePromoConfig();

  // Sort by metadata.order, then alphabetical
  const allTiers = [...rawTiers]
    .sort((a, b) => {
      const aHas = a.order !== undefined;
      const bHas = b.order !== undefined;
      if (aHas && bHas) return a.order! - b.order!;
      if (aHas) return -1;
      if (bHas) return 1;
      return a.name.localeCompare(b.name);
    })
    .filter((t) => isTestMode || t.active !== false);

  // Split enterprise out of the grid
  const enterpriseTier = allTiers.find((t) => t.ctaUrl.startsWith('mailto:'));
  const gridTiers = allTiers.filter((t) => !t.ctaUrl.startsWith('mailto:'));

  const hasYearlyOption = gridTiers.some((t) => t.yearlyPrice > 0);

  if (isLoading) {
    return (
      <div className="bodykit-theme min-h-screen bg-[#050a14] flex items-center justify-center">
        <LoadingSpinner size="w-8 h-8" label="Loading pricing..." />
      </div>
    );
  }

  if (allTiers.length === 0) {
    return (
      <div className="bodykit-theme min-h-screen bg-[#050a14] flex items-center justify-center">
        <p className="text-muted-foreground">We are having an issue loading subscription data.</p>
      </div>
    );
  }

  const colClass =
    gridTiers.length <= 3
      ? 'md:grid-cols-3'
      : gridTiers.length === 4
        ? 'md:grid-cols-2 lg:grid-cols-4'
        : 'md:grid-cols-3';

  return (
    <div className="bodykit-theme min-h-screen bg-[#050a14] text-foreground">
      {!authLoading && authenticated && <AppHeader />}

      {/* Unauthenticated header */}
      {!authLoading && !authenticated && (
        <header className="flex items-center justify-between px-6 py-3 border-b border-border">
          <a href="https://guardrail.tech" className="flex items-center gap-2">
            <TrafficLightLogo size="sm" showSub />
          </a>
          <nav className="hidden md:flex items-center gap-6 text-sm text-muted-foreground">
            <a href="https://guardrail.tech/pricing" className="text-primary font-medium">Pricing</a>
            <a href="https://guardrail.tech/support" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">Support</a>
            <a href="https://guardrail.tech/faq" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">FAQ</a>
            <a href="https://guardrail.tech/leaderboard" target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">Leaderboard</a>
          </nav>
          <a
            href="/login"
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            Log In
          </a>
        </header>
      )}

      {/* ============================================================= */}
      {/* Hero Section — deep navy bg with gradient glow */}
      {/* ============================================================= */}
      <div className="relative overflow-hidden">
        {/* Abstract gradient glow background */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse 80% 50% at 50% 0%, rgba(0,207,255,0.08) 0%, transparent 60%)',
          }}
        />

        <div className="container mx-auto px-4 pt-16 pb-6 max-w-7xl relative z-10">
          {/* Header */}
          <div className="text-center mb-4">
            {isTestMode && (
              <BkBadge variant="destructive" className="flex items-center gap-1 w-fit mx-auto mb-4">
                <FlaskConical className="w-3 h-3" />
                Sandbox
              </BkBadge>
            )}

            {/* Cyan label */}
            <p className="text-primary text-sm font-semibold uppercase tracking-[0.2em] mb-4">
              ✦ Pricing
            </p>

            <h1 className="text-4xl md:text-5xl lg:text-[56px] font-bold text-foreground mb-5 uppercase tracking-tight">
              Simple, Transparent Pricing
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Choose the plan that fits your security needs. All plans include core scanning features.
            </p>

            {/* Trial banner */}
            <div className="mt-6 inline-block rounded-full border border-primary/30 bg-primary/10 px-6 py-2">
              <span className="text-primary font-semibold text-sm">
                Every plan starts with a {promo.trialLabel} free trial — sign up today!
              </span>
            </div>

            {/* Billing toggle — bodykit TogglePillGroup */}
            {hasYearlyOption && (
              <div className="flex items-center justify-center gap-3 mt-8">
                <TogglePillGroup
                  value={yearly ? 'annual' : 'monthly'}
                  onChange={(v) => setYearly(v === 'annual')}
                  options={[
                    { value: 'monthly', label: 'Monthly' },
                    { value: 'annual', label: 'Annual' },
                  ]}
                />
                <BkBadge className="px-3 py-1 text-xs font-semibold">
                  Save 40%
                </BkBadge>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ============================================================= */}
      {/* Pricing Cards Grid */}
      {/* ============================================================= */}
      <div className="container mx-auto px-4 pb-10 max-w-7xl">
        <div className={`grid grid-cols-1 ${colClass} gap-6`}>
          {gridTiers.map((tier) => (
            <TierCard
              key={tier.tier}
              tier={tier}
              yearly={yearly}
              testMode={isTestMode}
              currentTier={currentTier}
              isTrial={isTrial}
              onSubscribe={(t, interval) =>
                navigate(`/subscribe/${t}${interval === 'year' ? '?billing=yearly' : ''}`)
              }
            />
          ))}
        </div>
      </div>

      {/* ============================================================= */}
      {/* Enterprise Section — WHITE background */}
      {/* ============================================================= */}
      {enterpriseTier && <EnterpriseSection tier={enterpriseTier} />}

      {/* ============================================================= */}
      {/* FAQ — back to dark */}
      {/* ============================================================= */}
      {faq.length > 0 && (
        <div className="container mx-auto px-4 py-20 max-w-3xl">
          <h2 className="text-2xl font-bold text-foreground text-center mb-8">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {faq.map((item, i) => (
              <BkCard key={i} className="p-5">
                <h3 className="text-base font-semibold text-foreground mb-2">{item.question}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{item.answer}</p>
              </BkCard>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
